import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import { AddCardDialog } from '../dialogs/AddCardDialog'
import { usePaymentMethods } from '@/hooks/usePaymentMethods'

const cardBrands: Record<string, { name: string; icon: string }> = {
  visa: { name: 'Visa', icon: '💳' },
  mastercard: { name: 'Mastercard', icon: '💳' },
  amex: { name: 'American Express', icon: '💳' },
  discover: { name: 'Discover', icon: '💳' },
}

export function PaymentMethodsSection() {
  const {
    cards: creditCards,
    loading,
    error,
    isUpdating,
    refetch,
    removeCard,
    setDefaultCard: setDefaultCardInHook,
  } = usePaymentMethods()

  const [addCardDialogOpen, setAddCardDialogOpen] = useState(false)
  const [deleteCardId, setDeleteCardId] = useState<string | null>(null)

  const handleSetDefaultCard = (cardId: string) => {
    void setDefaultCardInHook(cardId).catch(() => {
      /* Displayed by the hook. */
    })
  }

  const handleDeleteCard = async () => {
    if (!deleteCardId) return
    try {
      await removeCard(deleteCardId)
      setDeleteCardId(null)
    } catch {
      // Keep the confirmation open and show the hook's error.
    }
  }

  const formatCardExpiry = (month: number, year: number) => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`
  }

  return (
    <div className="flex flex-col gap-6">
      <Card className="border-0 shadow-none">
        <CardHeader>
          <CardTitle>Payment Methods</CardTitle>
          <CardDescription>Add and manage your credit cards for payments.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Credit Cards Section */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-medium text-foreground">Credit & Debit Cards</h3>
              <Button variant="outline" size="sm" onClick={() => setAddCardDialogOpen(true)}>
                <Icon name="plus" className="mr-1.5 h-3.5 w-3.5" />
                Add Card
              </Button>
            </div>

            {error && (
              <div role="alert" className="mb-3 flex items-center justify-between gap-3">
                <p className="text-sm text-destructive">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refetch}
                  disabled={loading || isUpdating}
                >
                  Retry payment methods
                </Button>
              </div>
            )}
            {loading ? (
              <p className="py-6 text-sm text-muted-foreground">Loading payment methods…</p>
            ) : error && creditCards.length === 0 ? null : creditCards.length === 0 ? (
              <div className="flex items-center justify-center rounded-lg border-2 border-dashed py-8">
                <div className="text-center">
                  <Icon name="credit-card" className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">No cards added</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {creditCards.map((card) => (
                  <div
                    key={card.id}
                    className={cn(
                      'flex items-center justify-between rounded-lg border p-3 transition-all',
                      card.isDefault ? 'bg-primary/5 border-primary' : 'bg-muted/50 border-border'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{cardBrands[card.brand]?.icon || '💳'}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground">
                            {cardBrands[card.brand]?.name || 'Card'} •••• {card.last4}
                          </p>
                          {card.isDefault && (
                            <Badge variant="default" className="h-4 px-1.5 text-[10px]">
                              Default
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Expires {formatCardExpiry(card.expiryMonth, card.expiryYear)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      {!card.isDefault && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={isUpdating}
                          onClick={() => handleSetDefaultCard(card.id)}
                          className="h-7 px-2 text-xs"
                        >
                          Set as default
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove card ending in ${card.last4}`}
                        disabled={isUpdating}
                        onClick={() => setDeleteCardId(card.id)}
                        className="h-7 w-7 p-0"
                      >
                        <Icon name="trash-2" className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Security Notice */}
          <div className="flex gap-2 rounded-lg border border-blue-200/50 bg-blue-50/50 p-3 dark:border-blue-900/50 dark:bg-blue-950/50">
            <Icon
              name="shield-check"
              className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400"
            />
            <div>
              <p className="mb-0.5 text-xs font-medium text-blue-900 dark:text-blue-100">
                Your payment information is secure
              </p>
              <p className="text-xs leading-relaxed text-blue-700 dark:text-blue-300">
                We use industry-standard encryption to protect your payment data. Card details are
                securely stored with our PCI-compliant payment processor.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      {addCardDialogOpen && (
        <AddCardDialog open={addCardDialogOpen} onOpenChange={setAddCardDialogOpen} />
      )}

      {/* Delete Card Confirmation */}
      <AlertDialog open={!!deleteCardId} onOpenChange={(open) => !open && setDeleteCardId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove card?</AlertDialogTitle>
            <AlertDialogDescription>
              This card will be removed from your account. You can add it again later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isUpdating}
              onClick={(event) => {
                event.preventDefault()
                void handleDeleteCard()
              }}
              className="hover:bg-destructive/90 bg-destructive text-destructive-foreground"
            >
              Remove card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
